import {Typed} from "app/stimulus/typed-stimulus";
import {Controller} from "@hotwired/stimulus";
import {debounce} from "throttle-debounce";
import dayjs from "dayjs";

export default class extends Typed(Controller<HTMLElement>, {
    values: {
        personType: Number,
        birthRegistrationNumberParseUrl: String,
    },
    targets: {
        birthDate: HTMLInputElement,
        birthRegistrationNumber: HTMLInputElement,
        birthRegistrationNumberLabel: HTMLLabelElement,
        nationality: HTMLSelectElement,
        gender: HTMLSelectElement,
        isSignatureNeeded: HTMLInputElement
    },
    classes: [
        'requiredLabel'
    ] as const
})
{
    public initialize(): void {
        this.onBirthRegistrationNumberChange = debounce(500, this.onBirthRegistrationNumberChange.bind(this)) as unknown as () => Promise<void>;
    }

    public connect(): void {
        this.setupBirthDate();
    }

    public onNationalityChange(): void {
        this.setupBirthDate();
    }

    public onIsSignatureNeededChange(): void {
        this.setupBirthDate();
    }

    private setupBirthDate(): void {
        if (!this.hasNationalityTarget) {
            return;
        }
        const nationality = this.nationalityTarget.value.toUpperCase();
        let isRequired = true;
        if (nationality !== 'CZ' && nationality !== 'SK') {
            isRequired = false;
        }
        if (this.isInactivePersonActingForCompany()) {
            isRequired = false;
        }
        if (this.hasBirthRegistrationNumberTarget) {
            isRequired
                ? this.birthRegistrationNumberTarget.setAttribute('required', 'required')
                : this.birthRegistrationNumberTarget.removeAttribute('required');
        }
        if (this.hasBirthRegistrationNumberLabelTarget) {
            this.birthRegistrationNumberLabelTarget.classList.toggle(this.requiredLabelClass, isRequired);
        }
    }

    public async onBirthRegistrationNumberChange(): Promise<void> {
        if (!this.hasBirthRegistrationNumberTarget || !this.hasBirthRegistrationNumberParseUrlValue) {
            return;
        }
        const birthRegistrationNumber = this.birthRegistrationNumberTarget.value.trim();
        if (birthRegistrationNumber === '') {
            return;
        }
        try {
            const response = await fetch(this.birthRegistrationNumberParseUrlValue, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                    birthRegistrationNumber: birthRegistrationNumber,
                }),
            });
            if (response.status !== 200) {
                console.error('Invalid response status: ' + response.status);
                return;
            }
            const parsedResponse: ParseBirthRegistrationNumberResponse = await response.json();
            if (this.hasBirthDateTarget && !this.birthDateTarget.disabled && this.birthDateTarget.value.trim() === '') {
                const date = dayjs(parsedResponse.date, 'YYYY-MM-DD');
                this.birthDateTarget.value = date.format('DD.MM.YYYY');
                this.birthDateTarget.dispatchEvent(new Event('change', {bubbles: true}));
            }
            if (this.hasGenderTarget && !this.genderTarget.disabled && typeof parsedResponse.gender === 'string') {
                this.genderTarget.value = parsedResponse.gender.toUpperCase();
                this.genderTarget.dispatchEvent(new Event('change', {bubbles: true}));
            }
        } catch (error) {
            console.error('Error parsing birth registration number:', error);
        }
    }

    private isStatutoryOrganMember(): boolean {
        return this.hasPersonTypeValue && this.personTypeValue === PersonType.StatutoryOrganMember;
    }

    private isAuthorisedPersonToActForCompany(): boolean {
        return this.hasPersonTypeValue && this.personTypeValue === PersonType.AuthorisedPersonToActForCompany;
    }

    private isPersonActingForCompany(): boolean {
        return this.isStatutoryOrganMember() || this.isAuthorisedPersonToActForCompany();
    }

    private isInactivePersonActingForCompany(): boolean {
        return this.isPersonActingForCompany() && !this.isSignatureNeeded();
    }

    private isSignatureNeeded(): boolean {
        return this.hasIsSignatureNeededTarget ? this.isSignatureNeededTarget.checked : false;
    }
}

type ParseBirthRegistrationNumberResponse = {
    date: string,
    gender?: 'male' | 'female',
}

enum PersonType
{
    StatutoryOrganMember = 1,
    AuthorisedPersonToActForCompany = 2,
    Owner = 3,
    AccountDisposer = 4,
    ToBeVerifiedPerson = 5
}
